import axios from 'axios';
import CONFIG from "../config";
import _ from 'underscore';
import Log from '../Log';
import queryString from "query-string";

/**
 * Resource range specification object:
 * @typedef {Object} ResourceRange
 * @property {Array} sort Sort fields
 * @property {number} first First resource index (starting from 0)
 * @property {number} last Last resource index (starting from 0)
 * @property {number} total Total record count in this collection
 * @property {boolean} estimated Is the total record count exact number or estimated number (if null then exact number)
 * @property {number} limit Query was initially limited by this record count (if 0 or missing then no limit)
 * @property {Object} filter Filter applied to query
 */

/**
 * RestApi resource collection object:
 * @typedef {Object} ResourceCollection
 * @property {Array} items Resource objects.
 * @property {ResourceRange} range Range specification
 */

/**
 * RestApi response object:
 * @typedef {Object} RestApiResponse
 * @property {string} requestId Request ID
 * @property {number} code Response status code
 * @property {number} message Response status message
 * @property {string} data Response data in case of non-json response
 * @property {Object} resource Returned resource object in case of single-resource requests.
 * @property {ResourceCollection} resourceCollection Returned resource collection object in case of search requests.
 * @property {ResourceRange} range Range specification from Content-Range header
 */

/**
 * RestApi error response object
 * @typedef {Object} RestApiErrorResponse
 * @property {string} requestId Request ID
 * @property {number} code Response status code
 * @property {string} reason Error reason code
 * @property {string} message Human-readable error message
 * @property {string} internalMessage Additional information about the message
 * @property {ResourceRange} range Range specification from Content-Range header
 */

/**
 * REST API implementation.
 *
 * Usage:
 *  api = new RestApi(baseUrl);
 *  api.get(table, id)
 *      // response is RestApiResponse object
 *      .then(function (response) {})
 *      // error is RestApiErrorResponse object
 *      .catch(function (error) {});
 *
 * Available methods:
 *      getCollection(table, params, sort, start, count)
 *      get(table, id)
 *      post(table, data)
 *      put(table, id, data)
 *      del(table, id)
 *
 */
class RestApi {
    /** @namespace result.error.internalDescription */
    /**
     * Constructor
     * @param baseUrl Base URL to append for each request
     * @param tokenLoader Callback used for reading authorization token
     */
    constructor(baseUrl = undefined, tokenLoader = null) {
        this.axios = axios.create();
        // eslint-disable-next-line
        if (baseUrl != null) this.axios.defaults.baseURL = baseUrl;
        this.axios.defaults.timeout = CONFIG.restApiTimeout;
        this.axios.defaults.headers.post["Content-Type"] = "application/json";
        this.axios.defaults.headers.put["Content-Type"] = "application/json";
        // We do not use default params serializer because default implementation uses brackets for array values.
        // Our current REST API server implementation does not support brackets.
        this.axios.defaults.paramsSerializer = function(params) {
            return queryString.stringify(params);
        };
        this.tokenLoader = tokenLoader;
    }

    setBaseUrl(baseUrl) {
        this.axios.defaults.baseURL = baseUrl;
    }

    setAuthToken(token) {
        if (token != null)
            this.axios.defaults.headers.common["X-Authorization"] = "Bearer " + token;
        else
            delete this.axios.defaults.headers.common["X-Authorization"];
    }

    getAuthorizationHeader() {
        return this.axios.defaults.headers.common["X-Authorization"];
    }

    setRequestId(requestId) {
        this.requestId = requestId;
        return this;
    }

    /**
     *
     * @param table Resource name
     * @param params Filter parameters
     * @param sort Sort specification - array of sort fields or single sort field. In order to get descending sort order prefix the field name with "-"
     * @param start First returned record (starting from 0)
     * @param count Maximum number of records to return
     * @param headers Additional headers
     * @returns {Promise}
     */
    getCollection(table, params, sort = undefined, start = undefined, count = undefined, headers = {}) {
        //let headers = {};
        if (CONFIG.useRangeHeader) {
            let rangeHeader = this.createRangeHeader(sort, start, count);
            if (rangeHeader) headers["Range"] = rangeHeader;
        } else {
            let addParams = this.createRangeParams(sort, start, count);
            params = _.extend({}, params, addParams);
        }
        return this.request('get', this.requestUrl(table), params, null, headers);
    }

    get(table, id, headers = null) {
        return this.request('get', this.requestUrl(table, id), null, null, headers);
    }

    put(table, id, data, headers = null) {
        return this.request('put', this.requestUrl(table, id), null, {resource: data}, headers);
    }

    post(table, data, headers = null) {
        return this.request('post', this.requestUrl(table), null, {resource: data}, headers);
    }

    del(table, id, headers) {
        return this.request('delete', this.requestUrl(table, id), null, null, headers);
    }

    request(method, url, params = undefined, data = undefined, headers = undefined) {
        Log.info('Executing REST API: ' + method);

        let p = this.createRequest(this.requestId, method, url, params, data, headers);
        this.requestId = null;

        return p.then(function (response) {
            Log.debug('Received response');
            return this.createResponse(response);
        }.bind(this), function (error) {
            throw this.createErrorResponse(error);
        }.bind(this));
    }

    requestUrl(table, id = null, subtable = null, subid = null) {
        let result = "/" + table;
        // eslint-disable-next-line
        if (id != null) result += "/" + encodeURIComponent(id);
        if (subtable != null) result += "/" + encodeURIComponent(subtable);
        if (subid != null) result += "/" + encodeURIComponent(subid);
        return result;
    }

    createRequest(requestId, method, url, params, data, headers) {
        let req = {
            method: method,
            url: url,
            params: params,
            data: data,
            requestId: requestId
        };
        if (headers) {
            req.headers = _.extend({}, this.axios.defaults.headers.common, headers);
        }

        if (!this.getAuthorizationHeader() && this.tokenLoader) {
            let token = this.tokenLoader();
            if (token) this.setAuthToken(token);
        }
        return this.axios.request(req);
    }

    createResponse(response) {
        // Handle response:
        let result = {};
        if (response.config) result.requestId = response.config.requestId;
        result.code = response.status;
        result.message = response.statusText;

        if (_.isObject(response.data)) {
            /*
               Response content contains exactly one top level element and this can be:
                "error" - in case of errors
                "resource" - in case of single resource
                "resourceCollection" - in case of resource collection

             */
            let topLevelElement = null;
            let keys = _.allKeys(response.data);
            if (keys != null && keys.length === 1) topLevelElement = keys[0];
            if (topLevelElement != null) {
                if (topLevelElement.endsWith("Collection")) {
                    result.resourceCollection = response.data[topLevelElement];
                    if (!CONFIG.useRangeHeader)
                        result.range = this.parseRangeFields(result.resourceCollection);
                }
                else if (topLevelElement === "error") result.error = response.data.error;
                else result.resource = response.data[topLevelElement];
            } else {
                Log.error("Invalid REST response, body should contain exactly one top level element");
            }
        } else {
            result.data = response.data;
        }

        if (CONFIG.useRangeHeader && _.has(response.headers, "Content-Range"))
            result.range = this.parseRangeHeader(response.headers["Content-Range"]);
        return result;
    }

    createErrorResponse(error) {
        let result = {};
        Log.debug('Error response: ' + error.message);

        if (error.response) {
            result = this.createResponse(error.response);
            if (result.error) {
                Log.debug('   status: ' + result.code);
                if (result.error.status) Log.debug('   code: ' + result.error.status);
                if (result.error.internalDescription) Log.debug('   internal message: ' + result.error.internalDescription);
                if (result.error.trace) Log.debug('   dump: ' + result.error.trace);

                if (result.error.status) result.code = result.error.status;
                result.reason = result.error.reason;
                result.message = result.error.description;
                result.internalMessage = result.error.internalDescription;
                delete result.error;
            }
        } else {
            result.code = 0;
            result.message = 'Error executing request';
            result.internalMessage = error.message;
        }
        if (error.config) result.requestId = error.config.requestId;
        return result;
    }

    createRangeHeader(sort, start, count) {
        let result = this.createRangeSpec(sort, start, count);
        if (!result) return null;
        return "resources=" + result;
    }

    createRangeParams(sort, start, count) {
        let params = {};
        if (sort != null) params["sort"] = sort.join(",");
        if (start != null) params["start"] = start;
        if (count != null) params["limit"] = count;
        return params;
    }

    createRangeSpec(sort, start, count) {
        let result = "";
        if (sort) {
            if (!_.isArray(sort)) sort = [sort];
            result += "[" + sort.join(",") + "]";
        }

        if (!start) {
            start = 0;
            if (!count && !sort) return null;
        }
        result += start.toString();
        if (start >= 0 && count > 0) {
            result += "-" + (start + count - 1).toString();
        }
        return result;
    }

    parseRangeFields(resourceCollection) {
        let result = {};
        if (!resourceCollection) return result;
        result.first = 0;
        if (_.has(resourceCollection, "@start")) result.first = resourceCollection["@start"];
        if (_.has(resourceCollection, "@limit")) result.limit = resourceCollection["@limit"];
        if (_.has(resourceCollection, "@total")) result.total = resourceCollection["@total"];
        if (_.has(resourceCollection, "@estimated")) result.estimated = resourceCollection["@estimated"];
        if (resourceCollection.items) result.last = result.first + resourceCollection.items.length - 1;
        if (resourceCollection["@sort"]) {
            result.sort = resourceCollection["@sort"].replace(/ +/g, "").split(",");
        }
        if (resourceCollection["@filter"]) {
            try {
                result.filter = queryString.parse(resourceCollection["@filter"]);
            } catch (e) {
                Log.error("Error parsing response filter attribute: " + e.message);
            }
        }
        return result;
    }

    parseRangeHeader(range) {
        let result = {};
        if (!range) return result;

        // Parse range header:
        let rangeParts = range.split(" ");
        let rangeType = rangeParts[0];
        if (rangeType === "resources") {
            let rangeSpec = rangeParts[1];
            let regex = /^(?:\[(.*)])?(?:(\d+)?(?:-(\d+))?|\*)\/(\d+)$/;
            let match = regex.exec(rangeSpec);
            if (match[1]) {
                // Sort specification:
                result.sort = match[1].split(",");
            }
            if (match[4] !== "") {
                // Total count
                result.total = parseInt(match[4], 10);
            }
            if (match[2] !== "") {
                // Range start
                result.first = parseInt(match[2], 10);
                result.last = result.first;
            }
            if (match[3] !== "") {
                // Range end
                let last = parseInt(match[3], 10);
                if (match[2] === "") {
                    result.first = result.total - last;
                }
                result.last = last;
            }
        }
        return result;
    }
}

export default RestApi;
